import React from 'react';
import { Link } from 'react-router-dom';
import SitePage, { SectionHeader, Meander, MaskWords, GLYPHS } from './SiteChrome';
import { WORK } from './workData';
import AppsBand from './AppsBand';

// Home (spec §3): say what the studio is in one screen, prove it in three,
// route to Contact. All copy is final from the spec — do not paraphrase.
// Visual world: kinetic serif hero over sea-light caustics, glyphs that draw
// themselves, editorial work tiles, hover-preview pillars.

const PILLARS = [
  { anchor: 'web', name: 'Website & application design, build, and maintenance', line: 'Designed, built, and kept alive by the same hands.', img: '/ext/media/pillar-web.jpg' },
  { anchor: 'identity', name: 'Logo design and brand aesthetics', line: 'The mark, the palette, the type, and the rules that hold them together.', img: '/ext/media/pillar-identity.jpg' },
  { anchor: 'presence', name: 'Online presence and advertising', line: 'Profiles, listings, and campaigns that carry the brand, not dilute it.', img: '/ext/media/pillar-presence.jpg' },
  { anchor: 'content', name: 'Photography and content creation', line: 'Real pictures of the real work. No stock.', img: '/ext/media/pillar-content.jpg' },
  { anchor: 'copy', name: 'Brand messaging and copy', line: 'What you say, everywhere you say it.', img: '/ext/media/pillar-copy.jpg' },
];

const STEPS = [
  { name: 'See', num: '01', copy: 'Start with how you want to be seen. We look at what exists — mark, site, photos, words, channels — and name what holds together and what doesn\u2019t.' },
  { name: 'Build', num: '02', copy: 'Build the system: identity, site, photography, copy, channels. One point of view, one set of standards.' },
  { name: 'Stay', num: '03', copy: 'Stay on for updates, campaigns, and care. A presence is maintained, not launched.' },
];

const STRIP = [
  { glyph: GLYPHS.identity, name: 'Identity', line: 'A mark and a system, not just a logo file.' },
  { glyph: GLYPHS.web, name: 'Web', line: 'Sites and apps we design, build, and keep running.' },
  { glyph: GLYPHS.content, name: 'Presence', line: 'Photography, words, and advertising that match.' },
];

// Capability ticker — the slow marquee of disciplines (spec restraint
// already overridden by the client's "showy" direction).
const TICKER = [
  'Identity systems', 'Websites', 'Custom applications', 'Booking engines',
  'Photography', 'Voice agents', 'Brand copy', 'Admin consoles',
  'Campaigns', 'Packaging', 'Livery', 'Care plans',
];

function Ticker() {
  const run = TICKER.map((t, i) => (
    <React.Fragment key={i}><span>{t}</span><em aria-hidden="true">·</em></React.Fragment>
  ));
  return (
    <div className="ac-ticker" aria-label={`Disciplines: ${TICKER.join(', ')}`}>
      <div className="ac-ticker-track" aria-hidden="true">{run}{run}</div>
    </div>
  );
}

// Cursor-follow preview (KKPK pattern): the pillar thumb tracks the pointer.
// transform-only — the CSS reads --mx/--my and eases toward them.
function followCursor(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
}

export function WorkCard({ work, index, delay = 0 }) {
  return (
    <Link className="ac-workcard ac-reveal-scale" style={{ '--d': `${delay}s` }} to={`/work/${work.slug}`}>
      <div className={`ac-workcard-media ${work.image ? '' : 'placeholder'}`}>
        {index && <span className="ac-workcard-index">{index}</span>}
        {work.image
          ? <img src={work.image} alt={work.imageAlt || work.title} loading="lazy" />
          : <span aria-hidden="true">{work.placeholder}</span>}
      </div>
      <h3>{work.title}</h3>
      <p>{work.line}</p>
    </Link>
  );
}

// The drifting wave field behind the hero type — four sine lines swimming at
// different speeds (stroke-dashoffset drift, pure CSS animation).
function WaveField() {
  const wave = (y, amp) =>
    `M0 ${y} ` +
    Array.from({ length: 14 }, (_, i) =>
      `Q${i * 46 + 11.5} ${y - amp} ${i * 46 + 23} ${y} T${i * 46 + 46} ${y}`
    ).join(' ');
  return (
    <svg className="ac-hero-wavefield" viewBox="0 0 640 120" aria-hidden="true" fill="none"
      stroke="currentColor" strokeWidth="1">
      {[
        [24, 7], [56, 9], [88, 6], [112, 8],
      ].map(([y, amp]) => (
        <path key={y} d={wave(y, amp)} strokeDasharray="30 16" />
      ))}
    </svg>
  );
}

export default function SiteHome() {
  return (
    <SitePage>
      {/* 3.2 Hero — kinetic type over sea light; first paint is still instant */}
      <section className="ac-hero">
        <div className="ac-hero-caustics" style={{ backgroundImage: 'url(/ext/media/sea-caustics.jpg)' }} aria-hidden="true" />
        <div className="ac-lightwash" aria-hidden="true" />
        <WaveField />
        <div className="ac-wrap" style={{ width: '100%' }}>
          <div className="ac-hero-copy">
            <h1 aria-label="Your brand should look like it belongs together.">
              <MaskWords text="Your brand should look like it" />
              <span className="ac-mask-word">
                <span style={{ '--d': '0.42s' }}>
                  <span className="ac-underline">
                    belongs together.
                    <svg viewBox="0 0 320 14" aria-hidden="true" preserveAspectRatio="none">
                      <path d="M4 10 Q40 3 80 8 T160 7 T240 8 T316 6" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                    </svg>
                  </span>
                </span>
              </span>
            </h1>
            <p className="ac-rise" style={{ '--d': '0.8s' }}>
              We design the identity, build the site, shoot the work, and write
              the words — so every place people meet you feels like the same company.
            </p>
            <div className="ac-hero-ctas ac-rise" style={{ '--d': '1s' }}>
              <Link className="ac-btn" to="/contact">Start a project</Link>
              <Link className="ac-textlink" to="/work">See the work →</Link>
            </div>
          </div>
        </div>
        <div className="ac-scrollcue" aria-hidden="true">Scroll</div>
      </section>

      {/* Capability ticker — slow marquee between hero and proof */}
      <Ticker />

      {/* 3.3 Proof strip — glyphs draw themselves in */}
      <section className="ac-wrap" aria-label="Capabilities">
        <div className="ac-strip">
          {STRIP.map((s, i) => {
            const Glyph = s.glyph;
            return (
              <div className="ac-strip-cell ac-reveal" style={{ '--d': `${i * 0.12}s` }} key={s.name}>
                <Glyph className="ac-strip-glyph" />
                <strong>{s.name}</strong>
                <span>{s.line}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3.4 Selected work */}
      <section className="ac-section">
        <div className="ac-lightwash" aria-hidden="true" />
        <div className="ac-wrap" style={{ position: 'relative' }}>
          <SectionHeader title="Selected work." />
          <div className="ac-work-grid">
            {WORK.map((w, i) => (
              <WorkCard key={w.slug} work={w} index={`0${i + 1}`} delay={i * 0.12} />
            ))}
          </div>
          <p style={{ marginTop: 36 }}>
            <Link className="ac-textlink" to="/work">All work →</Link>
          </p>
        </div>
      </section>

      <Meander />

      {/* 3.5 Services overview — hover previews float the pillar imagery */}
      <section className="ac-section ac-section-wash">
        <div className="ac-wrap">
          <SectionHeader title="What we do." />
          <div className="ac-pillar-list">
            {PILLARS.map((p, i) => (
              <Link className="ac-pillar-row ac-reveal" style={{ '--d': `${i * 0.08}s` }} key={p.anchor} to={`/services#${p.anchor}`}
                onMouseMove={followCursor}>
                <span className="ac-pillar-num">0{i + 1}</span>
                <h3>{p.name}</h3>
                <span className="ac-pillar-line">{p.line}</span>
                <img className="ac-pillar-thumb" src={p.img} alt="" loading="lazy" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Apps capability strip — the foundation, mentioned where it earns it */}
      <AppsBand compact />

      {/* 3.6 Approach — progress line draws across the three steps */}
      <section className="ac-section">
        <div className="ac-wrap">
          <SectionHeader title="How it works." />
          <div className="ac-steps">
            {STEPS.map((s, i) => (
              <div className="ac-step ac-reveal" style={{ '--d': `${i * 0.18}s` }} key={s.name}>
                <span className="ac-ghost-num" aria-hidden="true">{s.num}</span>
                <h3>{s.name}<em>.</em></h3>
                <p>{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Meander />

      {/* 3.7 Studio note — the ampersand floats behind */}
      <section className="ac-section ac-section-wash ac-quote-band">
        <span className="ac-amp-watermark" aria-hidden="true">&amp;</span>
        <div className="ac-wrap ac-reveal">
          <blockquote className="ac-quote" style={{ margin: 0 }}>
            Anastasia &amp; Co. is a small studio, kept small on purpose. The person
            who designs your identity is the person who minds your site and reads
            your copy out loud before it ships. One point of view. No patchwork.
          </blockquote>
        </div>
      </section>
    </SitePage>
  );
}
